import React, { useState, useEffect, useRef } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';
import { getEventImageUrl } from '../../utils/eventImage';

const CONTROLLER = 'eventsController.php';
const A4_ASPECT_RATIO = 210 / 297;
const EVENT_IMAGE_WIDTH = 840;
const EVENT_IMAGE_HEIGHT = 1188;
const MAX_PAST_EVENT_PHOTOS = 10;
const MAX_PAST_PHOTO_DIMENSION = 1600;

const createEmptyPastEventPhoto = () => ({
  file: null,
  description: '',
  imagePath: '',
  preview: '',
});

const createEmptyEventCost = () => ({
  costId: undefined,
  eventId: undefined,
  category: 'General',
  description: '',
  costType: 'fixed',
  unitAmountChf: '',
});

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
};

const optimizeEventImage = (file) => new Promise((resolve, reject) => {
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(sourceUrl);
    const sourceAspectRatio = image.width / image.height;
    let cropWidth = image.width;
    let cropHeight = image.height;
    let cropX = 0;
    let cropY = 0;

    if (sourceAspectRatio > A4_ASPECT_RATIO) {
      cropWidth = image.height * A4_ASPECT_RATIO;
      cropX = (image.width - cropWidth) / 2;
    } else {
      cropHeight = image.width / A4_ASPECT_RATIO;
      cropY = (image.height - cropHeight) / 2;
    }

    const canvas = document.createElement('canvas');
    canvas.width = EVENT_IMAGE_WIDTH;
    canvas.height = EVENT_IMAGE_HEIGHT;
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Unable to process this image.'));
      return;
    }

    context.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      EVENT_IMAGE_WIDTH,
      EVENT_IMAGE_HEIGHT
    );
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to optimize this image.'));
        return;
      }

      resolve(new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      }));
    }, 'image/jpeg', 0.82);
  };

  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('Unable to read this image.'));
  };
  image.src = sourceUrl;
});

const optimizePastEventPhoto = (file) => new Promise((resolve, reject) => {
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(sourceUrl);
    const scale = Math.min(1, MAX_PAST_PHOTO_DIMENSION / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Unable to process this photo.'));
      return;
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to optimize this photo.'));
        return;
      }

      resolve(new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      }));
    }, 'image/jpeg', 0.82);
  };

  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('Unable to read this photo.'));
  };
  image.src = sourceUrl;
});

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'title', label: 'Title' },
  { key: 'startTime', label: 'Start Time', render: (val, row) => formatDateTime(val || row.start_time) },
  { key: 'endTime', label: 'End Time', render: (val, row) => formatDateTime(val || row.end_time) },
  { key: 'location', label: 'Location' },
  { key: 'capacity', label: 'Capacity', render: (val) => (val !== null && val !== undefined && val !== '' ? val : 'Unlimited') },
  { 
    key: 'paymentLink', 
    label: 'Payment Link',
    render: (val) => val ? (
      <a
        href={val}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-action-edit"
        title={val}
        aria-label={`Open payment link: ${val}`}
        style={{ display: 'inline-block', textDecoration: 'none' }}
      >
        Open
      </a>
    ) : '-'
  },
  {
    key: 'active',
    label: 'Event Status',
    render: (val) => ({ 1: 'Active', 0: 'Inactive', '-1': 'Archived' }[Number(val)] || 'Unknown'),
  },
];

const initialFormState = {
  title: '',
  startTime: '',
  endTime: '',
  location: '',
  description: '',
  memberPrice: '',
  nonMemberPrice: '',
  capacity: '',
  paymentLink: '',
  active: 1,
};

export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [savedImagePath, setSavedImagePath] = useState('');
  const [eventCosts, setEventCosts] = useState([]);
  const [newCostDraft, setNewCostDraft] = useState(createEmptyEventCost());
  const [pastEventPhotos, setPastEventPhotos] = useState([createEmptyPastEventPhoto()]);
  const pastPhotoPreviewUrls = useRef(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  useEffect(() => () => {
    pastPhotoPreviewUrls.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setEvents(res.events || []);
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  };

  const clearPastPhotoPreviews = () => {
    pastPhotoPreviewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    pastPhotoPreviewUrls.current.clear();
  };

  const normalizeEventCosts = () => eventCosts
    .filter((cost) => cost && (
      String(cost.description || '').trim()
      || String(cost.unitAmountChf ?? '').trim() !== ''
      || cost.costId
      || cost.eventId
    ))
    .map((cost) => ({
      costId: cost.costId || undefined,
      eventId: cost.eventId || undefined,
      category: (cost.category || 'General').trim() || 'General',
      description: (cost.description || '').trim(),
      costType: cost.costType === 'variable' ? 'variable' : 'fixed',
      unitAmountChf: Number(cost.unitAmountChf ?? 0),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const isUpdating = Boolean(editingId);

    const normalizedCosts = normalizeEventCosts();

    // Prepare payload matching backend eventsDataHandler expectations
    const payload = {
      ...form,
      capacity: form.capacity !== '' ? parseInt(form.capacity, 10) : null,
      memberPrice: form.memberPrice !== '' ? parseFloat(form.memberPrice) : 0.00,
      nonMemberPrice: form.nonMemberPrice !== '' ? parseFloat(form.nonMemberPrice) : 0.00,
      costs: normalizedCosts,
      eventCosts: normalizedCosts,
    };
    const requestPayload = imageFile ? new FormData() : payload;
    if (imageFile) {
      Object.entries(payload).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            requestPayload.append(`${key}[]`, '');
            return;
          }

          value.forEach((item, index) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              Object.entries(item).forEach(([itemKey, itemValue]) => {
                if (itemValue !== undefined && itemValue !== null) {
                  requestPayload.append(`${key}[${index}][${itemKey}]`, String(itemValue));
                }
              });
              return;
            }

            requestPayload.append(`${key}[${index}]`, String(item ?? ''));
          });
          return;
        }
        requestPayload.append(key, value === null ? '' : String(value));
      });
      requestPayload.append('image', imageFile);
    }

    let savedEvent;
    try {
      if (editingId) {
        savedEvent = await client.put(CONTROLLER, editingId, requestPayload);
      } else {
        savedEvent = await client.post(CONTROLLER, requestPayload);
      }
    } catch (err) {
      setError(err.message);
      return;
    }

    const eventId = editingId || savedEvent.event?.id;
    if (!eventId && pastEventPhotos.some((photo) => photo.file)) {
      setError('The event was saved, but its ID was not returned, so past photos could not be uploaded.');
      return;
    }

    setEditingId(eventId || null);
    setImageFile(null);
    setImagePreview('');
    setSavedImagePath(getEventImageUrl(
      savedEvent.event?.imagePath || savedEvent.event?.image_path || savedEvent.event?.image
    ));

    const photosToUpload = pastEventPhotos.filter((photo) => photo.file);
    if (photosToUpload.length > 0) {
      const pastPhotosPayload = new FormData();
      photosToUpload.forEach((photo) => {
        pastPhotosPayload.append('images[]', photo.file);
        pastPhotosPayload.append('descriptions[]', photo.description.trim());
      });

      try {
        await client.post(
          `${CONTROLLER}?id=${eventId}&action=past-events`,
          pastPhotosPayload
        );
      } catch (err) {
        setError(`Event saved, but past event photos were not saved: ${err.message}`);
        return;
      }
    }

    setSuccess(isUpdating ? 'Event updated successfully.' : 'Event created successfully.');
    if (!isUpdating) {
      setForm(initialFormState);
      setEditingId(null);
      setSavedImagePath('');
      setEventCosts([]);
      setNewCostDraft(createEmptyEventCost());
      clearPastPhotoPreviews();
      setPastEventPhotos([createEmptyPastEventPhoto()]);
    }
    await loadEvents();
  };

  const handleEdit = async (eventItem) => {
    clearPastPhotoPreviews();
    setEditingId(eventItem.id);
    setImageFile(null);
    setImagePreview('');
    setSavedImagePath(getEventImageUrl(eventItem.imagePath || eventItem.image_path || eventItem.image));
    let savedPastPhotos = eventItem.pastEvents || eventItem.pastEventPhotos || eventItem.past_event_photos || eventItem.past_events;
    if (!Array.isArray(savedPastPhotos)) {
      try {
        const response = await client.get(CONTROLLER, eventItem.id);
        savedPastPhotos = response.event?.pastEvents || [];
      } catch (err) {
        savedPastPhotos = [];
        setError(`Could not load saved past-event photos: ${err.message}`);
      }
    }
    const loadedPastPhotos = Array.isArray(savedPastPhotos)
      ? savedPastPhotos.slice(0, MAX_PAST_EVENT_PHOTOS).map((photo) => ({
        file: null,
        description: photo.description || '',
        imagePath: photo.imgPath || photo.imagePath || photo.image_path || photo.image || '',
        id: photo.id,
        preview: '',
      }))
      : [];
    setPastEventPhotos(loadedPastPhotos.length ? loadedPastPhotos : [createEmptyPastEventPhoto()]);
    const savedCosts = Array.isArray(eventItem.costs)
      ? eventItem.costs
      : Array.isArray(eventItem.eventCosts)
        ? eventItem.eventCosts
        : [];
    setEventCosts(
      savedCosts.length
        ? savedCosts.map((cost) => ({
            costId: cost.costId,
            eventId: cost.eventId,
            category: cost.category || 'General',
            description: cost.description || '',
            costType: cost.costType || 'fixed',
            unitAmountChf: cost.unitAmountChf ?? cost.unit_amount_chf ?? '',
          }))
        : []
    );
    setNewCostDraft(createEmptyEventCost());
    setForm({
      title: eventItem.title || '',
      startTime: toDateTimeLocal(eventItem.startTime || eventItem.start_time),
      endTime: toDateTimeLocal(eventItem.endTime || eventItem.end_time),
      location: eventItem.location || '',
      description: eventItem.description || '',
      memberPrice: eventItem.memberPrice !== undefined ? eventItem.memberPrice : '',
      nonMemberPrice: eventItem.nonMemberPrice !== undefined ? eventItem.nonMemberPrice : '',
      capacity: eventItem.capacity !== null && eventItem.capacity !== undefined ? eventItem.capacity : '',
      paymentLink: eventItem.paymentLink || eventItem.payment_link || '',
      active: eventItem.active !== undefined ? Number(eventItem.active) : 1,
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    event.target.value = '';
    setImageFile(null);
    setImagePreview('');
    setError('');
    try {
      const optimizedFile = await optimizeEventImage(file);
      setImageFile(optimizedFile);
      setImagePreview(URL.createObjectURL(optimizedFile));
    } catch (err) {
      setError(err.message);
      event.target.value = '';
    }
  };

  const handlePastPhotoChange = async (index, event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    event.target.value = '';
    setError('');
    try {
      const optimizedFile = await optimizePastEventPhoto(file);
      const preview = URL.createObjectURL(optimizedFile);
      pastPhotoPreviewUrls.current.add(preview);
      const oldPreview = pastEventPhotos[index]?.preview;
      if (oldPreview) {
        URL.revokeObjectURL(oldPreview);
        pastPhotoPreviewUrls.current.delete(oldPreview);
      }
      setPastEventPhotos((current) => current.map((photo, photoIndex) => (
        photoIndex === index
          ? { ...photo, file: optimizedFile, imagePath: '', preview }
          : photo
      )));
    } catch (err) {
      setError(err.message);
    }
  };

  const updatePastPhotoDescription = (index, description) => {
    setPastEventPhotos((current) => current.map((photo, photoIndex) => (
      photoIndex === index ? { ...photo, description } : photo
    )));
  };

  const addPastEventPhoto = () => {
    setPastEventPhotos((current) => (
      current.length < MAX_PAST_EVENT_PHOTOS
        ? [...current, createEmptyPastEventPhoto()]
        : current
    ));
  };

  const removePastEventPhoto = async (index) => {
    const photo = pastEventPhotos[index];
    try {
      if (photo.id && editingId) {
        await client.delete(
          `${CONTROLLER}?id=${editingId}&action=past-events&pastEventId=${photo.id}`
        );
      }
    } catch (err) {
      setError(`Could not remove past-event photo: ${err.message}`);
      return;
    }

    if (photo.preview) {
      URL.revokeObjectURL(photo.preview);
      pastPhotoPreviewUrls.current.delete(photo.preview);
    }
    setPastEventPhotos((current) => {
      const remaining = current.filter((_, photoIndex) => photoIndex !== index);
      return remaining.length ? remaining : [createEmptyPastEventPhoto()];
    });
  };

  const updateEventCostDraft = (field, value) => {
    setNewCostDraft((current) => ({ ...current, [field]: value }));
  };

  const addEventCost = () => {
    const trimmedDescription = (newCostDraft.description || '').trim();
    const trimmedCategory = (newCostDraft.category || 'General').trim() || 'General';
    const amountValue = String(newCostDraft.unitAmountChf ?? '').trim();
    const numericAmount = Number(newCostDraft.unitAmountChf ?? 0);

    if (!trimmedDescription && amountValue === '') {
      return;
    }

    setEventCosts((current) => [
      ...current,
      {
        ...newCostDraft,
        category: trimmedCategory,
        description: trimmedDescription,
        costType: newCostDraft.costType === 'variable' ? 'variable' : 'fixed',
        unitAmountChf: Number.isFinite(numericAmount) ? numericAmount : 0,
      },
    ]);
    setNewCostDraft(createEmptyEventCost());
  };

  const removeEventCost = (index) => {
    setEventCosts((current) => current.filter((_, costIndex) => costIndex !== index));
  };

  const clearForm = () => {
    setEditingId(null);
    setForm(initialFormState);
    setImageFile(null);
    setImagePreview('');
    setSavedImagePath('');
    setEventCosts([]);
    setNewCostDraft(createEmptyEventCost());
    clearPastPhotoPreviews();
    setPastEventPhotos([createEmptyPastEventPhoto()]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      setError('');
      await client.delete(CONTROLLER, id);
      setSuccess('Event deleted successfully.');
      await loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const visibleEvents = events
    .filter((event) => statusFilter === 'all' || String(Number(event.active)) === statusFilter)
    .sort((first, second) => {
      const comparison = sortBy === 'title'
        ? String(first.title || '').localeCompare(String(second.title || ''), undefined, { sensitivity: 'base' })
        : Number(first.id) - Number(second.id);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(field);
    setSortDirection('asc');
  };

  const renderEventTableHeader = (column) => {
    if (column.key === 'id' || column.key === 'title') {
      const field = column.key === 'title' ? 'title' : 'id';
      const label = field === 'title' ? 'Name' : 'ID';
      const isSorted = sortBy === field;
      const directionLabel = sortDirection === 'asc' ? 'ascending' : 'descending';

      return (
        <button
          type="button"
          onClick={() => toggleSort(field)}
          aria-label={`Sort by ${label}${isSorted ? ` ${directionLabel}` : ''}`}
          style={{ border: 0, padding: 0, background: 'none', color: 'inherit', font: 'inherit', fontWeight: 'inherit', cursor: 'pointer' }}
        >
          {label}{isSorted ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
        </button>
      );
    }

    if (column.key === 'active') {
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <span>{column.label}</span>
          <select
            aria-label="Filter events by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={{ maxWidth: '130px' }}
          >
            <option value="all">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
            <option value="-1">Archived</option>
          </select>
        </label>
      );
    }

    return column.label;
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Event Management</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && !editingId && <div className="admin-success">{success}</div>}
      </div>

      <div className="admin-table-section events-manager-table" style={{ marginBottom: '24px' }}>
        <DataTable
          columns={columns}
          data={visibleEvents}
          onEdit={handleEdit}
          onDelete={handleDelete}
          renderHeader={renderEventTableHeader}
        />
      </div>

      <form onSubmit={handleSubmit} className="admin-form-container" style={{ display: 'grid', gap: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', minWidth: 0 }}>
            <h3
              className="form-section-title"
              style={{ margin: 0 }}
            >
              {editingId ? `Editing Event #${editingId}` : 'Create Event'}
            </h3>
            {editingId && success && <div className="admin-success" role="status">{success}</div>}
          </div>
          {editingId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button type="submit" className="btn btn-submit-professionals">
                Update Event
              </button>
              <button type="button" className="btn btn-cancel" onClick={clearForm} aria-label="Close event editor">
                Close editor
              </button>
            </div>
          )}
        </div>

        <section
          style={{
            background: '#fff',
            border: '1px solid #e1e4e8',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}
        >
          <div style={{ background: '#263746', borderBottom: '1px solid #263746', padding: '14px 18px' }}>
            <h3 className="form-section-title" style={{ margin: 0, fontSize: '1.1rem', letterSpacing: '0.03em', color: '#fff' }}>Event Information</h3>
          </div>
          <div className="grid-2-col" style={{ padding: '18px' }}>

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            placeholder="e.g. Networking Evening"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            placeholder="e.g. Lausanne"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Start Time *</label>
          <input
            type="datetime-local"
            required
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>End Time</label>
          <input
            type="datetime-local"
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Member Price (CHF)</label>
          <input
            type="number"
            step="0.01"
            placeholder="e.g. 0.00"
            value={form.memberPrice}
            onChange={(e) => setForm({ ...form, memberPrice: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Non-Member Price (CHF)</label>
          <input
            type="number"
            step="0.01"
            placeholder="e.g. 50.00"
            value={form.nonMemberPrice}
            onChange={(e) => setForm({ ...form, nonMemberPrice: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Total Capacity / Seats</label>
          <input
            type="number"
            min="0"
            placeholder="e.g. 80"
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Payment Link</label>
          <input
            type="url"
            placeholder="https://buy.stripe.com/..."
            value={form.paymentLink}
            onChange={(e) => setForm({ ...form, paymentLink: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="event-status">Event Status</label>
          <select
            id="event-status"
            value={form.active}
            onChange={(e) => setForm({ ...form, active: Number(e.target.value) })}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
            <option value={-1}>Archived</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="event-image">Event Image (A4 portrait)</label>
          <input
            id="event-image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          <span style={{ fontSize: '12px', color: '#68717d' }}>
            Cropped to A4 portrait and optimized for mobile display.
          </span>
          {(imagePreview || savedImagePath) && (
            <img
              src={imagePreview || savedImagePath}
              alt="Event preview"
              style={{
                display: 'block',
                width: 'min(100%, 187px)',
                maxHeight: '240px',
                objectFit: 'contain',
                objectPosition: 'left center',
                marginTop: '8px',
              }}
            />
          )}
        </div>

        <div className="form-group" style={{ gridColumn: 'span 2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label htmlFor="event-description">Description</label>
            <span style={{ fontSize: '12px', color: '#888' }}>
              {form.description.length}/1000
            </span>
          </div>
          <textarea
            id="event-description"
            rows="4"
            maxLength={1000}
            placeholder="Event summary and agenda (max 1000 characters)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
          </div>
        </section>

        <section
          style={{
            background: '#fff',
            border: '1px solid #e1e4e8',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}
        >
          <div style={{ background: '#263746', borderBottom: '1px solid #263746', padding: '14px 18px' }}>
            <h3 className="form-section-title" style={{ margin: 0, fontSize: '1.1rem', letterSpacing: '0.03em', color: '#fff' }}>Event Costs</h3>
          </div>
          <div style={{ display: 'grid', gap: '16px', padding: '18px' }}>
            {eventCosts.length > 0 ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {eventCosts.map((cost, index) => (
                  <div
                    key={`${cost.costId || 'new'}-${index}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      border: '1px solid #e1e4e8',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      background: '#fafbfc',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{cost.category || 'General'}</div>
                      <div style={{ fontSize: '12px', color: '#59636f' }}>
                        {cost.costType === 'variable' ? 'Variable' : 'Fixed'} • CHF {Number(cost.unitAmountChf ?? 0).toFixed(2)}
                      </div>
                      {cost.description && (
                        <div style={{ fontSize: '12px', color: '#59636f' }}>{cost.description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-cancel"
                      onClick={() => removeEventCost(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '12px', border: '1px dashed #d0d5da', borderRadius: '8px', color: '#68717d' }}>
                No costs added yet.
              </div>
            )}

            <div style={{ borderTop: '1px solid #e1e4e8', paddingTop: '16px', display: 'grid', gap: '12px' }}>
              <div style={{ fontWeight: 600 }}>Add new cost</div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Category</label>
                <input
                  type="text"
                  placeholder="General"
                  value={newCostDraft.category}
                  onChange={(e) => updateEventCostDraft('category', e.target.value)}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Cost Type</label>
                <select
                  value={newCostDraft.costType}
                  onChange={(e) => updateEventCostDraft('costType', e.target.value)}
                >
                  <option value="fixed">Fixed</option>
                  <option value="variable">Variable</option>
                </select>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Unit Amount (CHF)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newCostDraft.unitAmountChf}
                  onChange={(e) => updateEventCostDraft('unitAmountChf', e.target.value)}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Description</label>
                <textarea
                  rows="2"
                  placeholder="e.g. Venue rental or per-person catering"
                  value={newCostDraft.description}
                  onChange={(e) => updateEventCostDraft('description', e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn btn-submit-professionals"
                onClick={addEventCost}
              >
                + Add cost
              </button>
            </div>
          </div>
        </section>

        <section
          style={{
            background: '#fff',
            border: '1px solid #e1e4e8',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}
        >
          <div style={{ background: '#263746', borderBottom: '1px solid #263746', padding: '14px 18px' }}>
            <h3 className="form-section-title" style={{ margin: 0, fontSize: '1.1rem', letterSpacing: '0.03em', color: '#fff' }}>Past Event Information</h3>
          </div>
          <div style={{ display: 'grid', gap: '16px', padding: '18px' }}>
            {pastEventPhotos.map((photo, index) => (
              <div
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '16px',
                  alignItems: 'start',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #e1e4e8',
                }}
              >
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor={`past-event-photo-${index}`}>Photo {index + 1}</label>
                  <input
                    id={`past-event-photo-${index}`}
                    type="file"
                    accept="image/*"
                    onChange={(event) => handlePastPhotoChange(index, event)}
                  />
                  {(photo.preview || photo.imagePath) && (
                    <img
                      src={photo.preview || getEventImageUrl(photo.imagePath)}
                      alt={`Past event ${index + 1}`}
                      style={{ display: 'block', width: 'min(100%, 180px)', maxHeight: '180px', objectFit: 'contain', marginTop: '8px' }}
                    />
                  )}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor={`past-event-description-${index}`}>Photo description</label>
                  <input
                    id={`past-event-description-${index}`}
                    type="text"
                    value={photo.description}
                    readOnly={Boolean(photo.id)}
                    onChange={(event) => updatePastPhotoDescription(index, event.target.value)}
                  />
                </div>
                {pastEventPhotos.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => removePastEventPhoto(index)}
                    aria-label={`Remove past event photo ${index + 1}`}
                    style={{ gridColumn: 'span 2', justifySelf: 'end' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-submit-professionals"
              onClick={addPastEventPhoto}
              disabled={pastEventPhotos.length >= MAX_PAST_EVENT_PHOTOS}
              aria-label="Add a past event photo"
            >
              + Add photo ({pastEventPhotos.length}/{MAX_PAST_EVENT_PHOTOS})
            </button>
          </div>
        </section>

        <div className="btn-actions-container" style={{ justifyContent: 'flex-end' }}>
          {!editingId && (
            <button type="submit" className="btn btn-submit-professionals">
              Add Event
            </button>
          )}
        </div>
      </form>
    </div>
  );
}